import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  TablePagination,
  IconButton,
  Chip,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import type { UserAuditEvent, ConsolidatedAuditEvent } from '../types';
import type { PagedResult } from '../types/pagedResult';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import ClearIcon from '@mui/icons-material/Clear';
import { formatDate } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, pagination, displayTimeZoneCopy } from '../utils/userFacingCopy';

export default function UserAuditLogPage() {
  const { user, displayTimeZoneId } = useAuth();
  const isAdmin = user?.roles.includes('Administrator') ?? false;
  const [auditEvents, setAuditEvents] = useState<UserAuditEvent[]>([]);
  const [consolidatedEvents, setConsolidatedEvents] = useState<ConsolidatedAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination.auditLogDefaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  // Debounce search to avoid too many API calls
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 400);

    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  const loadAuditEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      params.append('page', (page + 1).toString()); // API uses 1-based pages
      params.append('pageSize', rowsPerPage.toString());
      if (debouncedSearchQuery.trim()) {
        params.append('searchQuery', debouncedSearchQuery.trim());
      }

      if (isAdmin) {
        // Admins use the original user audit events endpoint
        if (eventTypeFilter && eventTypeFilter !== 'all') {
          params.append('eventTypeFilter', eventTypeFilter);
        }

        const response = await apiService.get<PagedResult<UserAuditEvent>>(
          `${API_ENDPOINTS.USERS.AUDIT_EVENTS}?${params.toString()}`
        );
        setAuditEvents(response.data.items);
        setConsolidatedEvents([]);
        setTotalCount(response.data.totalCount);
      } else {
        // Non-admins use consolidated audit events (includes check-in, review, and user events)
        if (categoryFilter && categoryFilter !== 'all') {
          params.append('categoryFilter', categoryFilter);
        }

        const response = await apiService.get<PagedResult<ConsolidatedAuditEvent>>(
          `${API_ENDPOINTS.USERS.CONSOLIDATED_AUDIT_EVENTS}?${params.toString()}`
        );
        setConsolidatedEvents(response.data.items);
        setAuditEvents([]);
        setTotalCount(response.data.totalCount);
      }
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('audit events')));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, rowsPerPage, debouncedSearchQuery, eventTypeFilter, categoryFilter]);

  useEffect(() => {
    loadAuditEvents();
  }, [loadAuditEvents]);

  const getEventTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      // User events
      RoleTemplateChanged: 'Role Template Changed',
      RoleTemplateChangeManagerAcknowledged: 'Role Template Change Acknowledged (Manager)',
      RoleTemplateChangeEmployeeAcknowledged: 'Role Template Change Acknowledged (Employee)',
      RoleTemplateStateChanged: 'Role Template State Changed',
      RoleTemplateSubmittedForApproval: 'Role Template Submitted for Approval',
      RoleTemplateApproved: 'Role Template Approved',
      CheckInFrequencySet: 'Check-In Frequency Set',
      CheckInsOptedOut: 'Check-Ins Opted Out',
      CheckInsOptedIn: 'Check-Ins Opted In',
      CheckInsOptOutReaffirmed: 'Check-Ins Opt-Out Reaffirmed',
      UserRolesChanged: 'User Roles Changed',
      UserManagerChanged: 'Manager Changed',
      UserDepartmentChanged: 'Department Changed',
      UserBusinessUnitChanged: 'Business Unit Changed',
      UserCompanyChanged: 'Company Changed',
      UserNameChanged: 'Name Changed',
      UserActivated: 'User Activated',
      UserDeactivated: 'User Deactivated',
      UserCreated: 'User Created',
      UserSignedIn: 'User Signed In',
      UserProfileUpdated: 'Profile Updated',
      LeaveRequestApproved: 'Leave Request Approved',
      LeaveRequestSubmitted: 'Leave Request Submitted',
      LeaveRequestWasApproved: 'Leave Request Was Approved',
      LeaveRequestSeniorApproved: 'Leave Request Senior Approved',
      LeaveRequestWasSeniorApproved: 'Leave Request Was Senior Approved',
      LeaveRequestWithdrawn: 'Leave Request Withdrawn',
      LeaveRequestCreatedOnBehalf: 'Leave Request Created On Behalf',
      EvidenceUploaded: 'Leave Supporting Document Uploaded',
      EvidenceDeleted: 'Leave Supporting Document Deleted',
      EmailPreferencesChanged: 'Email Preferences Changed',
      EmailOptOutAllAgreed: 'Email Opt-Out Agreed',
      EmailOptOutReaffirmed: 'Email Opt-Out Reaffirmed',
      GovernanceAndComplianceAcknowledged: 'Governance and Compliance Acknowledged',
      GovernanceComplianceTextChanged: 'Governance Compliance Text Changed',
      EmailOptOutAcknowledgmentTextChanged: 'Email Opt-Out Acknowledgment Text Changed',
      CheckInOptOutAcknowledgmentTextChanged: 'Check-In Opt-Out Acknowledgment Text Changed',
      // Check-in events
      ConfigCreated: 'Config Created',
      ConfigUpdated: 'Config Updated',
      ConfigDeactivated: 'Config Deactivated',
      CheckInScheduled: 'Check-in Scheduled',
      ReminderSent: 'Reminder Sent',
      ReminderSentUrgent: 'Urgent Reminder Sent',
      ManagerOccurrenceConfirmed: 'Manager Occurrence Confirmed',
      EmployeeOccurrenceConfirmed: 'Employee Occurrence Confirmed',
      ManagerMarkedMissed: 'Manager Marked Missed',
      ManagerMarkedLate: 'Manager Marked Late',
      EmployeeAcknowledgedMissed: 'Employee Acknowledged Missed',
      EmployeeAcknowledgedLate: 'Employee Acknowledged Late',
      // Review events
      SelfReviewSubmitted: 'Self Review Submitted',
      SelfReviewUpdatedAfterSubmit: 'Self Review Updated (After Submit)',
      ReviewNotSubmittedDueToCutoff: 'Not Submitted (Cutoff)',
      ManagerReviewSubmitted: 'Manager Review Submitted',
      DiscussionAcknowledged: 'Discussion Acknowledged',
      ReviewReopened: 'Review Reopened',
      ReviewFinalised: 'Review Finalised',
      DisputeSubmitted: 'Dispute Submitted',
      DisputeWithdrawn: 'Dispute Withdrawn',
      DisputeResolved: 'Dispute Resolved',
      PeerNominationApproved: 'Peer Nomination Approved',
      PeerNominationRejected: 'Peer Nomination Rejected',
      EmployeeActivatedPeerNominations: 'Peer reviewers invited (employee)',
      PeerRatingSubmitted: 'Peer Rating Submitted',
      ObjectiveMeasuresChanged: 'Objective Measures Changed',
      ObjectiveWeightsApplied: 'Objective Weights Applied',
      ObjectiveMeasureAdded: 'Objective Measure Added',
      ObjectiveMeasureUpdated: 'Objective Measure Updated',
      ObjectiveMeasureWeightingChanged: 'Objective Measure Weighting Changed',
      ObjectiveMeasureDeleted: 'Objective Measure Deleted',
      // IDP events
      IdpCreated: 'IDP Created',
      IdpSubmitted: 'IDP Submitted',
      IdpAcknowledged: 'IDP Acknowledged',
      IdpApproved: 'IDP Acknowledged',
      QuarterlyReviewSubmitted: 'Quarterly Review Submitted',
      IdpRevertedToDraftForReview: 'IDP Reverted to Draft (Review)',
      IdpUpdated: 'IDP Updated',
      SupportingDocumentUploaded: 'Supporting Document Uploaded',
      SupportingDocumentDeleted: 'Supporting Document Deleted',
      SupportingDocumentUpdated: 'Supporting Document Updated',
      ReviewSupportingDocumentUploaded: 'Review Supporting Document Uploaded',
      ReviewSupportingDocumentDeleted: 'Review Supporting Document Deleted',
      ReviewSupportingDocumentUpdated: 'Review Supporting Document Updated',
      // Expense claim events (from consolidated log – enum names)
      ExpenseClaimSubmitted: 'Expense Claim Submitted',
      ExpenseClaimApproved: 'Expense Claim Approved',
      ExpenseClaimRejected: 'Expense Claim Rejected',
      ExpenseClaimWithdrawn: 'Expense Claim Withdrawn',
      ExpenseClaimRevised: 'Expense Claim Revised',
      ExpenseClaimCreatedOnBehalf: 'Expense Claim Created On Behalf',
      DocumentUploaded: 'Expense Supporting Document Uploaded',
      DocumentDeleted: 'Expense Supporting Document Deleted',
    };
    if (labels[eventType]) return labels[eventType];
    // Fallback: split CamelCase into words and capitalize (e.g. "SomeEventType" -> "Some Event Type")
    return eventType
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const eventTypes = [
    'all',
    'RoleTemplateChanged',
    'RoleTemplateChangeManagerAcknowledged',
    'RoleTemplateChangeEmployeeAcknowledged',
    'RoleTemplateStateChanged',
    'RoleTemplateSubmittedForApproval',
    'RoleTemplateApproved',
    'ObjectiveMeasureAdded',
    'ObjectiveMeasureUpdated',
    'ObjectiveMeasureWeightingChanged',
    'ObjectiveMeasureDeleted',
    'CheckInFrequencySet',
    'CheckInsOptedOut',
    'CheckInsOptedIn',
    'CheckInsOptOutReaffirmed',
    'UserRolesChanged',
    'UserManagerChanged',
    'UserDepartmentChanged',
    'UserBusinessUnitChanged',
    'UserCompanyChanged',
    'UserNameChanged',
    'UserActivated',
    'UserDeactivated',
    'UserCreated',
    'UserSignedIn',
    'UserProfileUpdated',
    'EmailPreferencesChanged',
    'EmailOptOutAllAgreed',
    'EmailOptOutReaffirmed',
    'GovernanceAndComplianceAcknowledged',
    'GovernanceComplianceTextChanged',
    'EmailOptOutAcknowledgmentTextChanged',
    'CheckInOptOutAcknowledgmentTextChanged',
  ];

  const categories = ['all', 'CheckIn', 'Review', 'User', 'IDP', 'Leave', 'ExpenseClaim'];

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(0); // Reset to first page on search
  };

  const handleEventTypeFilterChange = (value: string) => {
    setEventTypeFilter(value);
    setPage(0); // Reset to first page on filter change
  };

  const handleCategoryFilterChange = (value: string) => {
    setCategoryFilter(value);
    setPage(0); // Reset to first page on filter change
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
  };

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <HistoryIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            {isAdmin ? 'User Audit Log' : 'Audit Log'}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          {isAdmin
            ? 'This page shows all user-related audit events across the system for compliance reporting.'
            : 'This page shows all your personal audit events, including check-ins, reviews, and changes to your profile, role template, and other account-related activities.'}{' '}
          {displayTimeZoneCopy.auditLogTimesHint}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Search audit events"
                placeholder="Search by user, actor, or details..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                        sx={{ mr: -1 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {isAdmin ? (
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Event Type</InputLabel>
                  <Select
                    value={eventTypeFilter}
                    label="Event Type"
                    onChange={(e) => handleEventTypeFilterChange(e.target.value)}
                  >
                    {eventTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type === 'all' ? 'All Events' : getEventTypeLabel(type)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Category"
                    onChange={(e) => handleCategoryFilterChange(e.target.value)}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {((isAdmin && (searchQuery.trim() !== '' || eventTypeFilter !== 'all')) ||
              (!isAdmin && (searchQuery.trim() !== '' || categoryFilter !== 'all'))) && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchQuery.trim() !== '' && (
                  <Chip
                    label={`Search: "${searchQuery}"`}
                    onDelete={handleClearSearch}
                    size="small"
                    color="primary"
                  />
                )}
                {isAdmin && eventTypeFilter !== 'all' && (
                  <Chip
                    label={`Event Type: ${getEventTypeLabel(eventTypeFilter)}`}
                    onDelete={() => handleEventTypeFilterChange('all')}
                    size="small"
                    color="info"
                  />
                )}
                {!isAdmin && categoryFilter !== 'all' && (
                  <Chip
                    label={`Category: ${categoryFilter}`}
                    onDelete={() => handleCategoryFilterChange('all')}
                    size="small"
                    color="info"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table key={displayTimeZoneId}>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Timestamp</strong></TableCell>
                    {!isAdmin && <TableCell><strong>Category</strong></TableCell>}
                    <TableCell><strong>Event Type</strong></TableCell>
                    {isAdmin && <TableCell><strong>User</strong></TableCell>}
                    <TableCell><strong>Actor</strong></TableCell>
                    <TableCell><strong>Details</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {((isAdmin && auditEvents.length === 0) || (!isAdmin && consolidatedEvents.length === 0)) ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 5 : 5} align="center" sx={{ py: 0, border: 'none' }}>
                        <Box sx={{ py: 4 }}>
                          <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Audit Events Found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchQuery || (isAdmin ? eventTypeFilter !== 'all' : categoryFilter !== 'all')
                              ? 'Try adjusting your search or filter criteria to find what you\'re looking for.'
                              : isAdmin
                              ? 'No user audit events have been recorded yet. Events will appear here as users are created, updated, and managed.'
                              : 'No audit events have been recorded yet. Events will appear here as you participate in check-ins, reviews, and account changes.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : isAdmin ? (
                    auditEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>{formatDate(event.eventAt, { includeTime: true })}</TableCell>
                        <TableCell>{getEventTypeLabel(event.eventType)}</TableCell>
                        <TableCell>{event.userName}</TableCell>
                        <TableCell>{event.actorName}</TableCell>
                        <TableCell>{event.details || '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    consolidatedEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>{formatDate(event.eventAt, { includeTime: true })}</TableCell>
                        <TableCell>
                          <Chip label={event.eventCategory} size="small" color={
                            event.eventCategory === 'CheckIn' ? 'primary' :
                            event.eventCategory === 'Review' ? 'secondary' :
                            event.eventCategory === 'IDP' ? 'info' :
                            event.eventCategory === 'Leave' ? 'warning' :
                            event.eventCategory === 'ExpenseClaim' ? 'success' : 'default'
                          } />
                        </TableCell>
                        <TableCell>{getEventTypeLabel(event.eventType)}</TableCell>
                        <TableCell>{event.actorName}</TableCell>
                        <TableCell>{event.details || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[...pagination.auditLogPageSizeOptions]}
              />
            </>
        )}
      </Box>
    </AppLayout>
  );
}
